import { collection, getDocs, limit, orderBy, query, where, updateDoc, doc, getDoc } from 'firebase/firestore';
import { AlertCircle, Calendar, Calendar as CalendarIcon, ChevronDown, ChevronRight, Clock, ExternalLink, FileText, Hash, Plus, Sparkles, Users, Video, LayoutGrid, List } from 'lucide-react';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ScheduleModal from '../components/ScheduleModal';
import StudentCard from '../components/StudentCard';
import SyncStatusBanner from '../components/SyncStatusBanner';
import Tooltip from '../components/Tooltip';
import ProductTour from '../components/ProductTour';
import { db } from '../firebase';
import { useTranslation } from '../lib/translations';
import { Lesson, Relationship, UserProfile } from '../types';
import { format, isBefore } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { getFileIcon } from '../lib/fileUtils';

interface DashboardProps {
  profile: UserProfile | null;
}

const Dashboard: React.FC<DashboardProps> = ({ profile }) => {
  const [upcomingLessons, setUpcomingLessons] = useState<Lesson[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showTour, setShowTour] = useState(false);
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  const locale = language === 'fr' ? fr : enUS;

  // Tour logic: Teachers only
  useEffect(() => {
    if (profile && profile.role === 'teacher') {
      const forceShow = localStorage.getItem('force_show_tour');
      const localCompleted = localStorage.getItem(`tour_completed_${profile.uid}`);
      
      if (forceShow === 'true') {
        setShowTour(true);
        localStorage.removeItem('force_show_tour');
      } else if (profile.hasCompletedTour === undefined && !localCompleted) {
        setShowTour(true);
      }
    }
  }, [profile]);

  const handleTourComplete = async () => {
    setShowTour(false);
    if (profile) {
      localStorage.setItem(`tour_completed_${profile.uid}`, 'true');
      try {
        await updateDoc(doc(db, 'users', profile.uid), { hasCompletedTour: true });
      } catch (e) { console.error(e); }
    }
  };

  // Persistence of view mode
  useEffect(() => {
    const saved = localStorage.getItem('dashboard_view_mode') as 'grid' | 'list';
    if (saved) setViewMode(saved);
  }, []);

  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('dashboard_view_mode', mode);
  };

  const fetchData = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const lessonsRef = collection(db, 'lessons');
      const lessonQuery = query(
        lessonsRef, 
        where(profile.role === 'teacher' ? 'teacherId' : 'studentId', '==', profile.uid), 
        where('status', '==', 'scheduled'), 
        orderBy('start', 'asc'), 
        limit(15)
      );
      const lessonSnap = await getDocs(lessonQuery);
      const lessonsData = lessonSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson));
      setUpcomingLessons(lessonsData);

      const relQuery = query(
        collection(db, 'relationships'), 
        where(profile.role === 'teacher' ? 'teacherId' : 'studentId', '==', profile.uid), 
        where('status', '==', 'active')
      );
      const relSnap = await getDocs(relQuery);
      
      const enrichedRels = await Promise.all(relSnap.docs.map(async (d) => {
        const data = d.data() as Relationship;
        const partnerId = profile.role === 'teacher' ? data.studentId : data.teacherId;
        const isPending = partnerId.startsWith('pending:');
        
        let userData = null;
        if (!isPending) {
          const userDoc = await getDoc(doc(db, 'users', partnerId));
          userData = userDoc.data();
        }

        const email = isPending ? partnerId.split(':')[1] : (userData?.email || partnerId);
        const name = userData?.displayName || email.split('@')[0];

        return { 
          ...data, 
          id: d.id, 
          studentFirstName: userData?.firstName || name.split(' ')[0],
          studentLastName: userData?.lastName || '',
          studentName: isPending ? `${name} (${t.common.pending})` : name, 
          studentEmail: email, 
          studentPhoto: userData?.photoURL || '', 
          teacherFirstName: userData?.firstName || 'Teacher',
          teacherLastName: userData?.lastName || '',
          teacherName: userData?.displayName || 'Teacher' 
        };
      }));
      setRelationships(enrichedRels);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [profile]);

  // Sidebar list logic
  const sidebarRelationships = useMemo(() => {
    // For students, show all mentors
    if (profile?.role === 'student') return relationships;
    // For teachers, only show students with upcoming lessons
    return relationships.filter(rel => {
      const partnerId = rel.studentId;
      return upcomingLessons.some(lesson => lesson.studentId === partnerId);
    });
  }, [relationships, upcomingLessons, profile]);

  const toggleExpand = (id: string) => {
    setExpandedLessonId(expandedLessonId === id ? null : id);
  };

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-[60vh] gap-4">
      <div className="relative">
        <div className="w-12 h-12 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin"></div>
        <Hash size={18} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-400" />
      </div>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t.common.loading}</p>
    </div>
  );

  const now = new Date();

  return (
    <div className="space-y-10 max-w-6xl mx-auto">
      {showTour && profile?.role && (
        <ProductTour 
          role={profile.role} 
          onComplete={handleTourComplete} 
          onSkip={handleTourComplete} 
        />
      )}

      <SyncStatusBanner profile={profile} onReconnectSuccess={fetchData} />

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6" id="tour-welcome">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm">
            <Sparkles size={12} className="animate-pulse" /> {t.dashboard.performanceFeed}
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tighter">{t.dashboard.hi}, {profile?.firstName || profile?.displayName.split(' ')[0]}</h1>
          <p className="text-slate-500 font-medium text-lg">{t.dashboard.remainingSessions.replace('{count}', upcomingLessons.length.toString())}</p>
        </div>
        {profile?.role === 'teacher' && (
          <button onClick={() => setIsScheduleOpen(true)} className="flex items-center gap-3 bg-purple-600 text-white px-8 py-4 rounded-xl font-black text-sm transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 shadow-xl shadow-purple-500/20">
            <Plus size={20} /> {t.dashboard.newPerformance}
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-8" id="tour-schedule">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black flex items-center gap-3 tracking-tight text-slate-900 dark:text-white">
              <CalendarIcon size={24} className="text-purple-600" /> {t.dashboard.upcomingSchedule}
            </h2>
            <div className="h-px bg-slate-100 dark:bg-white/5 flex-1 mx-6 hidden md:block"></div>
          </div>
          <div className="space-y-4">
            {upcomingLessons.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-16 text-center shadow-sm">
                <CalendarIcon size={48} className="text-slate-100 dark:text-slate-800 mx-auto mb-4" />
                <h3 className="text-base font-bold text-slate-400 dark:text-600 italic">{t.dashboard.noSessions}</h3>
              </div>
            ) : (
              upcomingLessons.map((lesson) => {
                const relationship = relationships.find(r => 
                  profile?.role === 'teacher' ? r.studentId === lesson.studentId : r.teacherId === lesson.teacherId
                );
                const partnerName = relationship?.studentName || t.common.student;
                const isExpanded = expandedLessonId === lesson.id;
                const isOverdue = isBefore(lesson.start.toDate(), now);
                
                return (
                  <div key={lesson.id} className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden transition-all hover:shadow-xl hover:border-purple-500/30 group shadow-sm ${isOverdue ? 'ring-1 ring-red-500/30' : ''}`}>
                    <div 
                      onClick={() => toggleExpand(lesson.id!)}
                      className="p-6 flex items-center gap-5 cursor-pointer"
                    >
                      <div className={`
                        w-12 h-12 flex items-center justify-center rounded-lg transition-all duration-500
                        ${relationship?.color ? '' : 'bg-purple-50 dark:bg-purple-500/10'}
                      `} style={{ backgroundColor: relationship?.color ? `${relationship.color}15` : undefined }}>
                        <Calendar size={20} style={{ color: relationship?.color || '#9333ea' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1">
                          <h3 className="text-lg font-black text-slate-900 dark:text-white truncate group-hover:text-purple-600 transition-colors">{partnerName}</h3>
                          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">{format(lesson.start.toDate(), 'EEEE', { locale })}</span>
                        </div>
                        <h4 className="text-base font-black text-slate-900 dark:text-white leading-none mb-1.5">{format(lesson.start.toDate(), 'PPP', { locale })}</h4>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tighter">
                            <Clock size={12} className="opacity-50" /> {format(lesson.start.toDate(), 'p', { locale })}
                          </span>
                          {isOverdue ? (
                            <span className="bg-red-500/10 text-red-600 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.1em] border border-red-500/20 flex items-center gap-1">
                              <AlertCircle size={10} /> {t.common.due}
                            </span>
                          ) : (
                            <span className="bg-purple-500/10 text-purple-600 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.1em] border border-purple-500/20">{t.common.scheduled}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {lesson.status === 'scheduled' && lesson.meetLink && (
                          <a 
                            href={lesson.meetLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="h-10 px-4 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center gap-2 font-black text-[10px] uppercase tracking-widest hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all border border-indigo-200 dark:border-indigo-500/20"
                          >
                            <Video size={14} /> <span className="hidden sm:inline">{t.dashboard.joinMeet}</span>
                          </a>
                        )}
                        <div className={`p-2 rounded-full bg-slate-50 dark:bg-slate-800 text-slate-400 transition-all ${isExpanded ? 'rotate-180 bg-purple-50 dark:group-hover:bg-purple-900/30 text-purple-600' : ''}`}>
                          <ChevronDown size={20} />
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate(profile?.role === 'teacher' ? `/student/${lesson.studentId}` : `/student/${lesson.teacherId}`); }} 
                          className="h-12 w-12 rounded-xl bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-xl shadow-purple-500/20"
                          title={t.dashboard.openPortal}
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-6 pb-6 pt-2 border-t border-slate-50 dark:border-white/5 animate-in slide-in-from-top-4 duration-300">
                        <div className="bg-slate-50 dark:bg-slate-950/50 p-6 rounded-xl space-y-6">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                              <FileText size={14} className="text-purple-600" /> {t.dashboard.performanceNotes}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed font-medium">
                              {lesson.homework?.description || t.dashboard.detailsPending}
                            </p>
                          </div>
                          
                          {lesson.homework?.attachments?.length > 0 && (
                            <div className="space-y-3">
                              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t.dashboard.linkedResources}</div>
                              <div className="flex flex-wrap gap-2">
                                {lesson.homework.attachments.map((file, idx) => (
                                  <a 
                                    key={idx} 
                                    href={file.webViewLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/5 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-purple-600 hover:border-purple-500/30 transition-all shadow-sm"
                                  >
                                    {getFileIcon(file.mimeType)}
                                    {file.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="lg:col-span-4 space-y-8" id="tour-learners">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black flex items-center gap-3 tracking-tight text-slate-900 dark:text-white">
              <Users size={24} className="text-teal-600" /> {profile?.role === 'teacher' ? t.dashboard.upcomingLearners : t.dashboard.upcomingMentors}
            </h2>
            <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg p-1 shadow-sm">
              <Tooltip content={t.students.cardView}>
                <button 
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <LayoutGrid size={14} />
                </button>
              </Tooltip>
              <Tooltip content={t.students.listView}>
                <button 
                  onClick={() => handleViewModeChange('list')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <List size={14} />
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="space-y-4">
            {sidebarRelationships.length === 0 ? (
              <p className="text-sm text-slate-400 font-medium italic">{t.dashboard.noConnections}</p>
            ) : (
              sidebarRelationships.map(rel => (
                <StudentCard 
                  key={rel.id} 
                  relationship={rel} 
                  variant={viewMode}
                  onClick={() => navigate(`/student/${profile?.role === 'teacher' ? rel.studentId : rel.teacherId}`)} 
                />
              ))
            )}
          </div>
        </div>
      </div>
      <ScheduleModal isOpen={isScheduleOpen} onClose={() => { setIsScheduleOpen(false); fetchData(); }} profile={profile} />
    </div>
  );
};

export default Dashboard;